import mapboxgl, { Point } from 'mapbox-gl'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { LocationValue } from '@flowwai-frontend/shared/common-components'

import { MapCoordinates, MapStyleEnum } from '../types'
import { useMapsInstanceContext } from './useMapsInstanceContext'
import { useMapsUtilityContext } from './useMapsUtilityContext'

import '../styles/mapPopupStyles.css'

interface HookArgs {
   currentGeoShape: LocationValue
   isReadOnly: boolean
   validateAndUpdateGeoShape: (geoShape: LocationValue) => void
   closePopoverIfOpen: () => boolean
}

export const useMapsEventHandlers = (args: HookArgs): void => {
   const {
      isReadOnly,
      currentGeoShape,
      validateAndUpdateGeoShape,
      closePopoverIfOpen
   } = args

   const mapsInstance = useMapsInstanceContext()
   const { defaultViewMode, restrictToMapStyle } = useMapsUtilityContext()

   const popupRef = useRef<mapboxgl.Popup | null>(null)

   const isMapsInViewMode = !mapsInstance.isEditMode || isReadOnly

   const addPopupsToMap = (
      coordinates: MapCoordinates,
      point: Point
   ): boolean => {
      const feature = mapsInstance.getFeaturesForPoint?.(point, [
         'ghmc-lakes-local'
      ])[0]

      if (
         feature &&
         feature.properties != null &&
         typeof feature.properties == 'object'
      ) {
         let popupContent =
            '<div class="overflow-auto p-1 pt-4 map-geo-feature-popup">'
         for (const [key, value] of Object.entries(feature.properties)) {
            popupContent += `<div class="text-xs-semibold text-gray-900 flex align-center justify-between"><div class="shrink-0 p-1">${key}</div><span class="text-gray-500 truncate p-1">${value}</span></div>`
         }
         popupContent += '</div>'

         popupRef.current!.setLngLat(coordinates).setHTML(popupContent)

         mapsInstance.addPopupToMap?.(popupRef.current!)
         return true
      }
      const kmlFeature = mapsInstance.getFeaturesForPoint?.(point, [
         'kml-polygons',
         'kml-lines',
         'kml-points'
      ])[0]

      if (
         kmlFeature &&
         kmlFeature.properties != null &&
         typeof kmlFeature.properties == 'object'
      ) {
         let popupContent = '<div class="overflow-auto map-geo-feature-popup">'
         if (kmlFeature.properties.name) {
            popupContent += `<div class="text-xs-semibold text-gray-900 flex align-center justify-center"><span class="text-gray-900 font-bold text-center truncate p-1">${kmlFeature.properties.name}</span></div>`
         }
         popupContent += kmlFeature.properties.description || ''

         popupContent += '</div>'

         popupRef.current!.setLngLat(coordinates).setHTML(popupContent)

         mapsInstance.addPopupToMap?.(popupRef.current!)
         return true
      }

      const customKmlFeatures = mapsInstance.getFeaturesForPoint?.(point)

      if (customKmlFeatures?.length) {
         const clickedFeature = customKmlFeatures.find((feature: any) =>
            feature.layer.id.startsWith('bn')
         )

         if (clickedFeature) {
            let popupContent =
               '<div class="rounded-lg overflow-hidden map-geo-feature-popup">'

            popupContent += clickedFeature.properties?.Description as string

            popupContent += '</div>'

            popupRef.current!.setLngLat(coordinates).setHTML(popupContent)

            mapsInstance.addPopupToMap?.(popupRef.current!)
            return true
         }
      }

      return false
   }

   const onClickMap = (
      clickedCoordinates: MapCoordinates,
      point?: Point
   ): void => {
      const isPopoverClosed = closePopoverIfOpen()
      if (isPopoverClosed) return

      const isStreetViewEnabled = mapsInstance.mapStyle === MapStyleEnum.Street
      if (isStreetViewEnabled) {
         mapsInstance.streetViewLayer?.setPosition(clickedCoordinates)
         mapsInstance.streetViewLayer?.setVisible(true)
         return
      }

      const shouldAddPopups = point && isMapsInViewMode
      if (shouldAddPopups) {
         const hasPopups = addPopupsToMap(clickedCoordinates, point)
         if (hasPopups) return
      }

      if (isMapsInViewMode) return

      switch (currentGeoShape.type) {
         case 'POLYGON': {
            const newCoordinates = [...(currentGeoShape.value ?? [])]
            if (newCoordinates.length) {
               newCoordinates.splice(
                  newCoordinates.length - 1,
                  0,
                  clickedCoordinates
               )
            } else {
               newCoordinates.push(clickedCoordinates, clickedCoordinates)
            }

            validateAndUpdateGeoShape({
               type: 'POLYGON',
               value: newCoordinates
            })
            break
         }
         case 'LINE': {
            const updatedCoordinates = [
               ...(currentGeoShape.value ?? []),
               clickedCoordinates
            ]

            validateAndUpdateGeoShape({
               type: 'LINE',
               value: updatedCoordinates
            })
            break
         }
         case 'MULTI_POINT': {
            validateAndUpdateGeoShape({
               type: 'MULTI_POINT',
               value: [...(currentGeoShape.value ?? []), clickedCoordinates]
            })
            break
         }
         case 'POINT': {
            validateAndUpdateGeoShape({
               type: 'POINT',
               value: clickedCoordinates
            })
            break
         }
      }
   }

   const resetMapOnMount = () => {
      const resetToMapEditMode = isReadOnly ? false : defaultViewMode === 'EDIT'
      mapsInstance.setIsEditMode(resetToMapEditMode)
   }

   const resetMapOnUnMount = () => {
      const defaultMapStyle = restrictToMapStyle ?? MapStyleEnum.Default
      mapsInstance.onChangeMapStyle(defaultMapStyle)
      mapsInstance.streetViewLayer?.setVisible(false)
   }

   useEffect(() => {
      mapsInstance.registerOnClick(onClickMap)

      return () => mapsInstance.unRegisterOnClick()
   }, [onClickMap])

   useLayoutEffect(() => {
      resetMapOnMount()

      popupRef.current = new mapboxgl.Popup({
         closeButton: true,
         closeOnClick: true,
         className: 'max-h-50 overflow-auto'
      })

      return resetMapOnUnMount
   }, [])
}
